import React, { ReactNode, Suspense, createContext, useContext, useMemo, useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { BrowserRouter } from 'react-router-dom'

import { BrandSpinner } from 'asserts/DynamicIcons/BrandSpinner'
import { localStorageConstants } from 'constants/localstorageConstants'
import { UserPermission, UserSettingsData } from 'modules/pages/manageSettings/managePermission'
import { getFromStorage } from 'services/helper'

type RolePermission = {
  moduleName: string
  permissionName: string
  permissionDescription: string
  permissionLabel: string
  permissionId: number | null
  rolePermissionId: number | null
}
type Role = {
  RoleID: number | null
  RoleName: string
  RoleDescription: string
  permissions: RolePermission[]
}

type loginUserData = {
  Active: boolean
  DisplayName: string | null
  Role: Role
}

export type appContextStoreType = {
  loginUserData: loginUserData
  userAccessMapping: UserSettingsData[]
  setUserAccessMapping: React.Dispatch<React.SetStateAction<UserSettingsData[]>>
  selectedSupplierStartDate?: string | null
  setSelectedSupplierStartDate: React.Dispatch<React.SetStateAction<string | null>>
  setLoginUserData: React.Dispatch<React.SetStateAction<loginUserData>>
  hasPermission: (permissionSlug: string[]) => boolean
  hasUserPermission: (permissionSlug: string[]) => boolean
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      refetchOnReconnect: true,
    },
  },
})

const defaultLoginData = {
  Active: false,
  DisplayName: null,
  Role: {
    RoleID: null,
    RoleName: '',
    RoleDescription: '',
    permissions: [],
  },
}

export const AppContext = createContext<appContextStoreType>({
  loginUserData: defaultLoginData,
  userAccessMapping: [],
  selectedSupplierStartDate: null,
  setSelectedSupplierStartDate: () => {},
  setUserAccessMapping: () => {},
  setLoginUserData: () => {},
  hasPermission: () => false,
  hasUserPermission: () => false,
})

export const useAppContext = () => {
  const data: appContextStoreType = useContext(AppContext)
  return data
}

export const AppProviders = ({ children }: { children: ReactNode }) => {
  const [loginUserData, setLoginUserData] = useState<loginUserData>(defaultLoginData)
  const [userAccessMapping, setUserAccessMapping] = useState<UserSettingsData[]>([])
  const [selectedSupplierStartDate, setSelectedSupplierStartDate] = useState<string | null>(null)

  const hasPermission = (permissionSlug: string[]) => {
    return loginUserData?.Role?.permissions?.some(({ permissionName }) => permissionSlug.includes(permissionName))
  }
  const hasUserPermission = (permissionSlug: string[]) => {
    const selectedSupplierId =
      getFromStorage(localStorageConstants?.CUSTOMER_FILTER)?.[0]?._id || userAccessMapping?.[0]?.ScrapDragonSupplierID || ''
    const currentUserPermission =
      userAccessMapping?.find(
        ({ ScrapDragonSupplierID }: UserSettingsData) => ScrapDragonSupplierID === selectedSupplierId,
      ) ?? ({} as Partial<UserSettingsData>)
    const {
      UserSupplierSettingsID = null,
      UserSupplierSettingsValue = false,
      SupplierSettingsValue = false,
    } = currentUserPermission?.settings?.find(({ SettingsName }: UserPermission) => permissionSlug.includes(SettingsName)) ??
    ({} as Partial<UserPermission>)
    return UserSupplierSettingsID ? UserSupplierSettingsValue : SupplierSettingsValue
  }
  const value = useMemo(
    () => ({
      loginUserData,
      setLoginUserData,
      userAccessMapping,
      setUserAccessMapping,
      selectedSupplierStartDate,
      setSelectedSupplierStartDate,
      hasPermission,
      hasUserPermission,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loginUserData, setLoginUserData, userAccessMapping, setUserAccessMapping, setSelectedSupplierStartDate],
  )
  return (
    <AppContext.Provider {...{ value }}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <Suspense
            fallback={
              <div className="fallback-loader">
                <BrandSpinner />
              </div>
            }
          >
            {children}
          </Suspense>
        </QueryClientProvider>
      </BrowserRouter>
    </AppContext.Provider>
  )
}
